import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { fmtRM, BRAND_LABELS } from "@/lib/api";
import { waLink, WhatsAppIcon } from "@/lib/whatsapp";

export default function SpecModal({ model, onClose }) {
  return (
    <AnimatePresence>
      {model && (
        <motion.div
          data-testid="spec-modal"
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm md:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            data-lenis-prevent
            className="aw-scroll max-h-[92vh] w-full max-w-2xl overflow-y-auto bg-white"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[16/9] overflow-hidden bg-neutral-100">
              <img src={model.image} alt={model.name} className="img-bw h-full w-full object-cover" />
              <button
                data-testid="spec-modal-close"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center bg-white text-black transition-colors hover:bg-black hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
              {model.badge && (
                <span className="absolute left-0 top-0 bg-red-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-black">
                  {model.badge}
                </span>
              )}
            </div>

            <div className="p-6 md:p-8">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                {BRAND_LABELS[model.brand]} · {model.body_type}
              </div>
              <h3 className="mt-1 font-display text-3xl font-extrabold uppercase tracking-tight md:text-4xl">
                {BRAND_LABELS[model.brand]} {model.name}
              </h3>
              <p className="mt-1 text-neutral-500">{model.tagline}</p>

              {model.variants?.length > 0 && (
                <div className="mt-6" data-testid="spec-variants">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Variants &amp; Direct Prices</div>
                  <div className="mt-2 divide-y divide-black/10 border-y border-black/10">
                    {model.variants.map((v) => (
                      <div key={v.name} className="flex items-center justify-between py-3">
                        <span className="text-sm font-medium">{v.name}</span>
                        <span className="font-display font-bold">{fmtRM(v.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {model.official_url ? (
                <div className="mt-6" data-testid="spec-official-link-block">
                  <a
                    href={model.official_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="spec-official-link"
                    className="group flex items-center justify-between border border-black p-5 transition-colors hover:bg-black hover:text-white"
                  >
                    <div>
                      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-500 group-hover:text-neutral-400">Full specs &amp; gallery</div>
                      <div className="mt-1 font-display text-lg font-bold">View on the official {BRAND_LABELS[model.brand]} site</div>
                    </div>
                    <span className="font-display text-2xl transition-transform group-hover:translate-x-1">→</span>
                  </a>
                  {model.specs?.engine && (
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                      {model.specs.engine} · {model.specs.power}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mt-6" data-testid="spec-table">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Full Specs</div>
                  <div className="mt-2 grid grid-cols-2 gap-px border border-black/10 bg-black/10">
                    {[
                      ["Engine", model.specs?.engine],
                      ["Power", model.specs?.power],
                      ["Torque", model.specs?.torque],
                      ["Transmission", model.specs?.transmission],
                      ["Fuel / Range", model.specs?.fuel_economy],
                      ["Seats", model.specs?.seats],
                      ["Warranty", model.specs?.warranty],
                      ["Body", model.body_type],
                    ].map(([k, v]) => (
                      <div key={k} className="bg-white p-3">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{k}</div>
                        <div className="mt-0.5 text-sm font-semibold">{v || "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {model.features?.length > 0 && (
                <div className="mt-6" data-testid="spec-features">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Key Features</div>
                  <ul className="mt-2 space-y-2">
                    {model.features.map((f) => (
                      <li key={f} className="flex gap-3 text-sm">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-black" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={waLink(`Hi AutoWorld! I'd like the best direct price for the ${BRAND_LABELS[model.brand]} ${model.name} (from ${fmtRM(model.price_from)}).`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="spec-modal-whatsapp"
                  className="flex flex-1 items-center justify-center gap-2 bg-black py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-neutral-800"
                >
                  <WhatsAppIcon className="h-4 w-4" /> Enquire via WhatsApp
                </a>
                <Link
                  to="/calculator"
                  data-testid="spec-modal-calculator"
                  className="flex flex-1 items-center justify-center border border-black py-4 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors hover:bg-black hover:text-white"
                >
                  Calculate Installment
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
